// @owners { team: patients }
import { Button, type ButtonProps, Row } from '@alto/design-system';
import { OrderEarlyCTA, useAddToCartFlow, useRemoveFromCart } from '@alto/features';
import { type Prescription } from '@alto/scriptdash/alto/patient_app/homescreen/types/v1/prescription';
import React, { useState } from 'react';
import { usePrescriptionsInCart } from '~shared/features/cart/hooks/usePrescriptionsInCart';
import { prescriptionToItemKey } from '~shared/features/checkout/helpers';
import { ORIGIN_NAMES } from '~shared/lib/analytics/src/getOrigin';

export type Props = {
  readonly fullWidthButton?: boolean;
  readonly isProgynyOrderEarlyEligible?: boolean;
  readonly prescription: Prescription;
};

export const AddToCart = ({ fullWidthButton, isProgynyOrderEarlyEligible, prescription }: Props) => {
  const { handleAddToCart } = useAddToCartFlow({ origin: ORIGIN_NAMES.HOMESCREEN });
  const { isPrescriptionInCart } = usePrescriptionsInCart();
  const { handleRemoveFromCart } = useRemoveFromCart();
  const [isLoading, setIsLoading] = useState(false);

  const inCart = isPrescriptionInCart(prescription.id);

  const handlePress = async () => {
    setIsLoading(true);
    try {
      if (inCart) {
        await handleRemoveFromCart({ item: prescriptionToItemKey(prescription.id) });
        return;
      }
      await handleAddToCart(prescription.id);
    } finally {
      setIsLoading(false);
    }
  };

  const generateButtonProps = (): ButtonProps => {
    const buttonProps = {
      small: true,
      fullWidth: fullWidthButton,
      label: 'Add to cart',
      onPress: () => {
        void handlePress();
      },
      loading: isLoading,
    };

    if (inCart) {
      return {
        ...buttonProps,
        type: 'secondary',
        label: 'Remove from cart',
      };
    }

    return buttonProps;
  };

  if (isProgynyOrderEarlyEligible) {
    return (
      <OrderEarlyCTA
        prescription={prescription}
        inlineButton
        showTag={false}
      />
    );
  }

  return (
    <Row left>
      <Button
        {...generateButtonProps()}
        testID="e2e-homescreen-add-to-cart"
      />
    </Row>
  );
};
